import * as React from 'react'

import { observer } from 'mobx-react'

import {
  OptionsHeader,
  OptionsPanel,
} from '~/client/src/shared/components/OptionsList'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import {
  pictogramSlugOf,
  toPictogramIconName,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { iconNamesOfKind } from '~/client/src/shared/resources/domain/locationKind'

import { IIconCatalog } from './iconCatalog'
import { typeLabel } from './iconGroups'
import IconPickerList from './IconPickerList'
import { useLocationTypes } from './useLocationTypes'

const DEFAULT_TAB = 'default'
const LIBRARY_TAB = 'library'

function PickerTabs({
  defaultValue,
  ...props
}: React.ComponentProps<typeof Tabs>): React.ReactElement {
  const [tab, setTab] = React.useState(defaultValue)
  return <Tabs {...props} value={tab} onValueChange={setTab} />
}

interface IProps {
  value: string | null
  onChange: (value: string) => void
  catalog: IIconCatalog
  /** The location's type: its own icons make the Default tab; the Library lists every icon. */
  kind?: string | null
  disabled?: boolean
  isMobileMode?: boolean
  /** The trigger, e.g. `IconPickerTrigger`. */
  children: React.ReactElement
}

function IconPicker({
  value,
  onChange,
  catalog,
  kind,
  disabled,
  isMobileMode,
  children,
}: IProps): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange
  // Stable, so the memoised tiles skip a pick: only the old and the new selection re-render.
  const pick = React.useCallback((iconName: string) => {
    onChangeRef.current(iconName)
    setOpen(false)
  }, [])
  const types = useLocationTypes()
  // Both tabs list the colourable StruxHub glyphs before the full-colour library icons.
  const [ownIconNames, libraryIconNames] = React.useMemo(() => {
    const names: string[] = [
      ...catalog.struxhub,
      ...catalog.custom.map(toPictogramIconName),
    ]
    const own = iconNamesOfKind(kind).filter(name => names.includes(name))
    return [
      [
        ...own.filter(name => !pictogramSlugOf(name)),
        ...own.filter(name => pictogramSlugOf(name)),
      ],
      names,
    ]
  }, [catalog, kind])

  if (disabled) {
    return React.cloneElement(children, { disabled: true })
  }

  const t = Localization.translator
  return (
    <ResponsiveSurface
      open={open}
      onOpenChange={setOpen}
      title={t.iconPicker.selectIcon}
      isMobileMode={isMobileMode}
      sizing="fill"
      width="picker"
      trigger={children}
    >
      <OptionsPanel className="tw:flex-1 tw:py-2.5">
        <div className="tw:px-2.5">
          <OptionsHeader
            title={t.iconPicker.selectIcon}
            onClose={() => setOpen(false)}
          />
        </div>
        {!catalog.custom.length ? (
          <IconPickerList
            iconNames={catalog.struxhub}
            isGrouped={false}
            types={types}
            value={value}
            onPick={pick}
          />
        ) : !ownIconNames.length ? (
          // One box at the surface's cap: a box that changed height with the search or a
          // collapsed group re-ran placement, which flipped the popover to another side.
          <div className="tw:flex tw:min-h-0 tw:grow tw:basis-[640px] tw:flex-col">
            <IconPickerList
              iconNames={libraryIconNames}
              isGrouped={true}
              types={types}
              value={value}
              onPick={pick}
            />
          </div>
        ) : (
          // Both tabs share that one box, so switching tabs does not move the popover either.
          <PickerTabs
            defaultValue={
              ownIconNames.includes(value) ? DEFAULT_TAB : LIBRARY_TAB
            }
            className="tw:min-h-0 tw:grow tw:basis-[640px] tw:gap-0"
          >
            <div className="tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-2.5">
              <TabsList variant="line" className="tw:w-full">
                <TabsTrigger value={DEFAULT_TAB}>{t.default}</TabsTrigger>
                <TabsTrigger value={LIBRARY_TAB}>
                  {t.iconPicker.library}
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value={DEFAULT_TAB}
              className="tw:flex tw:min-h-0 tw:flex-col"
            >
              <IconPickerList
                iconNames={ownIconNames}
                isGrouped={false}
                typeName={typeLabel(kind, types)}
                types={types}
                value={value}
                onPick={pick}
              />
            </TabsContent>
            <TabsContent
              value={LIBRARY_TAB}
              className="tw:flex tw:min-h-0 tw:flex-col"
            >
              <IconPickerList
                iconNames={libraryIconNames}
                isGrouped={true}
                types={types}
                value={value}
                onPick={pick}
              />
            </TabsContent>
          </PickerTabs>
        )}
      </OptionsPanel>
    </ResponsiveSurface>
  )
}

export default observer(IconPicker)
